import React, { useEffect, useMemo, useRef, useState } from "react";

import { ChevronDown } from "../../components/Icons";

import { CARD_SURFACE_STYLE, DashboardWindow } from "./dashboardHelpers";
import { ToggleSwitch } from "./ToggleSwitch";
import { useDismissOnOutside } from "./useDismissOnOutside";

/**
 * Curated Material Icons (ligature names) offered by the KPI icon picker. The iframe loads the
 * full Material Icons font, so any ligature name renders; this list just keeps the grid scannable.
 * The block's current icon is prepended at render time if it isn't in the list, so AI-authored
 * icons never disappear from the picker.
 */
const ICON_NAMES = [
  "attach_money",
  "paid",
  "payments",
  "monetization_on",
  "account_balance",
  "account_balance_wallet",
  "savings",
  "credit_card",
  "point_of_sale",
  "receipt_long",
  "request_quote",
  "price_check",
  "sell",
  "shopping_cart",
  "shopping_bag",
  "storefront",
  "trending_up",
  "trending_down",
  "trending_flat",
  "show_chart",
  "insights",
  "analytics",
  "bar_chart",
  "pie_chart",
  "donut_large",
  "leaderboard",
  "timeline",
  "speed",
  "percent",
  "numbers",
  "functions",
  "calculate",
  "person",
  "person_add",
  "group",
  "groups",
  "diversity_3",
  "badge",
  "handshake",
  "support_agent",
  "headset_mic",
  "call",
  "chat",
  "mail",
  "campaign",
  "notifications",
  "star",
  "favorite",
  "thumb_up",
  "verified",
  "emoji_events",
  "military_tech",
  "workspace_premium",
  "rocket_launch",
  "bolt",
  "local_fire_department",
  "eco",
  "public",
  "language",
  "schedule",
  "timer",
  "event",
  "calendar_month",
  "date_range",
  "update",
  "history",
  "task_alt",
  "check_circle",
  "cancel",
  "error",
  "warning",
  "info",
  "help",
  "visibility",
  "search",
  "filter_alt",
  "flag",
  "dashboard",
  "table_chart",
  "inventory_2",
  "category",
  "widgets",
  "build",
  "settings",
  "factory",
  "precision_manufacturing",
  "local_shipping",
  "flight",
  "directions_car",
  "home",
  "apartment",
  "location_on",
  "map",
  "layers",
  "cloud",
  "computer",
  "smartphone",
  "devices",
  "memory",
  "storage",
  "security",
  "lock",
  "key",
  "health_and_safety",
  "medical_services",
  "science",
  "school",
  "work",
  "business_center",
  "corporate_fare",
];

interface Props {
  icon: string;
  onChange: (icon: string) => void;
  /** Spec field `show_icon`. The "no icon" control lives in this panel's header, not the popover. */
  showIcon: boolean;
  onShowIconChange: (showIcon: boolean) => void;
  targetWindow: DashboardWindow;
}

/**
 * Icon picker for the KPI block editor: a 24px glyph and chevron that open a panel whose header
 * toggles the icon off entirely and whose body is a searchable Material Icons
 * grid. Icons render in the theme's card foreground — color is owned by the theme (and the
 * AI-only `icon_color` spec field), so there is no color control here.
 * Renders inside the dashboard iframe, so it relies on the iframe's Material Icons font and
 * Tailwind browser runtime.
 */
export const MetricIconPicker: React.FC<Props> = ({
  icon,
  onChange,
  showIcon,
  onShowIconChange,
  targetWindow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Capture-phase: the picker is nested inside BlockEditPopover, so it must close in its own
  // step rather than letting the click tear down the whole panel (see useDismissOnOutside).
  useDismissOnOutside(containerRef, () => setIsOpen(false), targetWindow, {
    enabled: isOpen,
    capture: true,
  });

  // Depends on showIcon too: the search field only exists while the icon is on, so toggling it
  // back on mounts the input and should focus it, exactly as opening the panel does.
  useEffect(() => {
    if (isOpen && showIcon) {
      searchInputRef.current?.focus();
    } else if (!isOpen) {
      setSearch("");
    }
  }, [isOpen, showIcon]);

  const icons = useMemo(() => {
    const all = ICON_NAMES.includes(icon) ? ICON_NAMES : [icon, ...ICON_NAMES];
    const query = search.trim().toLowerCase().replace(/\s+/g, "_");
    return query ? all.filter(name => name.includes(query)) : all;
  }, [icon, search]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-label="Choose icon"
        aria-expanded={isOpen}
        className="flex items-center gap-[6px] cursor-pointer text-card-foreground"
      >
        {/* 24px glyph + 16px chevron, per the Figma. Icon off: a strike-through glyph so the row
            reads as "no icon" without needing a separate label. */}
        <span className={`flex items-center justify-center ${showIcon ? "" : "opacity-40"}`}>
          <span className="material-icons !text-[24px]">{showIcon ? icon : "block"}</span>
        </span>
        <ChevronDown
          width={16}
          height={16}
          className={`text-card-muted-foreground transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          // 256px = the 280px popover minus its 12px gutters, so the grid never hangs outside it.
          className="absolute right-0 top-full mt-[4px] z-10 w-[256px] rounded-[8px] border border-card-border shadow-xl p-[12px] space-y-[8px]"
          style={CARD_SURFACE_STYLE}
        >
          {/* Turning the icon off is the "no icon" choice — it lives here rather than as its own
              popover row, so the icon controls stay in one place. */}
          <div className="flex items-center justify-between h-[24px]">
            <span className="text-[13px] leading-[18px] font-medium text-card-muted-foreground">
              Icon
            </span>
            <ToggleSwitch
              checked={showIcon}
              onChange={onShowIconChange}
              ariaLabel="Show icon on the card"
            />
          </div>

          {showIcon && (
            <>
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search icons..."
                className="w-full border border-card-foreground/20 rounded-[4px] px-[10px] py-[6px] text-[14px] text-card-foreground bg-card-background caret-primary-button-background focus:outline-none focus:ring-2 focus:ring-primary-button-background focus:border-transparent placeholder:text-card-muted-foreground"
              />

              <div className="max-h-[200px] overflow-y-auto">
                {icons.length === 0 ? (
                  <div className="text-[12px] text-card-muted-foreground text-center py-[16px]">
                    No icons match
                  </div>
                ) : (
                  <div className="grid grid-cols-6 gap-[2px]">
                    {icons.map(name => (
                      <button
                        key={name}
                        type="button"
                        title={name.replace(/_/g, " ")}
                        onClick={() => {
                          onChange(name);
                          setIsOpen(false);
                        }}
                        className={`flex items-center justify-center w-[32px] h-[32px] rounded-[6px] cursor-pointer text-card-foreground hover:bg-card-foreground/10 ${
                          name === icon ? "bg-card-foreground/15" : ""
                        }`}
                      >
                        <span className="material-icons !text-[18px]">{name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
